import { useEffect } from 'react';
import { useBranding } from '@/hooks/useBranding';

/**
 * useDocumentTitle
 *
 * Sets the browser tab title to `${title} · ${brand}` for surfaces that do not
 * go through the public SEO component (admin, account, internal pages).
 * Without this, those pages leave `document.title` untouched and the browser
 * falls back to displaying the raw URL.
 *
 * Restores the previous title on unmount so a stale heading never lingers.
 * No-ops when no usable string title is provided.
 */
export function useDocumentTitle(title?: string | null): void {
  const { brand } = useBranding();

  useEffect(() => {
    const trimmed = typeof title === 'string' ? title.trim() : '';
    if (!trimmed) return;

    const previous = document.title;
    document.title = `${trimmed} · ${brand.displayName}`;

    return () => {
      document.title = previous;
    };
  }, [title, brand.displayName]);
}
